import { useEffect, useRef, useMemo, useState } from 'react';
import { withAuthenticationRequired, useAuth0 } from '@auth0/auth0-react';
import { Link, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import './PrivateRoute.css';

/**
 * ProtectedComponent
 * 
 * The inner component that renders after authentication is confirmed.
 * Handles subscription checks and role-based access control.
 */
function ProtectedComponent({ children }) {
  const { user } = useAuth0();
  const toastShownRef = useRef(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  // Check for developer role - developers always have access
  const namespace = 'https://api.humanaize.nl';
  
  // Memoize roles to avoid recreating the array on every render
  const roles = useMemo(() => user?.[`${namespace}/roles`] || [], [user]);
  
  const isDeveloper = useMemo(() => roles.includes('developer'), [roles]);

  // Check subscription status from Auth0 token (added by post-login action)
  const subscription = user?.['https://api.humanaize.nl/subscription'];
  const hasAccess = subscription?.has_access;
  const status = subscription?.status || 'none';

  // Show toast notifications (all hooks at top level - no conditional hooks!)
  useEffect(() => {
    // Developer toast
    if (isDeveloper && !toastShownRef.current) {
      toast.success('👨‍💻 Developer access granted', {
        duration: 3000,
        position: 'top-right',
      });
      toastShownRef.current = true;
    }

    // Subscription error toasts (only if not developer)
    if (!isDeveloper && !hasAccess && status !== 'trialing' && !toastShownRef.current) {
      const hasJesseSubscribedRole = roles.includes('jesse_subscribed');

      if (!hasJesseSubscribedRole) {
        toast.error('🔒 Je mist de "jesse_subscribed" rol. Neem contact op met support.', {
          duration: 6000,
          position: 'top-center',
        });
      } else {
        toast.error('⏰ Je abonnement is verlopen. Vernieuw je abonnement om door te gaan.', {
          duration: 5000,
          position: 'top-center',
        });
      }
      toastShownRef.current = true;
    }
  }, [isDeveloper, hasAccess, status, roles]);

  // Handle authenticated checkout
  const handleAuthenticatedCheckout = async (e) => {
    e.preventDefault();
    setCheckoutLoading(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

      const response = await fetch(`${API_URL}/api/checkout/authenticated`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: user.email,
          name: user.name,
          auth0_sub: user.sub,
          plan: 'personal'
        })
      });

      const data = await response.json();

      // Handle existing subscription (409 Conflict)
      if (response.status === 409 && data.code === 'SUBSCRIPTION_EXISTS') {
        toast.success(data.message, { duration: 5000 });

        // If portal URL is provided, redirect to manage subscription
        if (data.portalUrl) {
          window.location.href = data.portalUrl;
        }
        setCheckoutLoading(false);
        return;
      }

      if (data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        toast.error(data.error || 'Kon checkout niet starten. Probeer het opnieuw.');
        setCheckoutLoading(false);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Er ging iets mis. Probeer het opnieuw.');
      setCheckoutLoading(false);
    }
  };

  // Developers bypass all subscription checks
  if (isDeveloper) {
    return children;
  }

  // If no access (trial expired, no subscription, etc.), show upgrade message
  if (!hasAccess && status !== 'trialing') {
    return (
      <div className="access-page">
        <div className="access-card">
          <span className="access-icon">
            {status === 'trial_expired' ? '⏰' : '🔒'}
          </span>
          <h1 className="access-title">
            {status === 'trial_expired' ? 'Proefperiode Verlopen' : 'Abonnement Vereist'}
          </h1>
          <p className="access-description">
            {status === 'trial_expired'
              ? 'Je 7-daagse gratis proefperiode is afgelopen. Start een abonnement om verder te gaan met Job.'
              : subscription?.access_reason || 'Je hebt een actief abonnement nodig om toegang te krijgen tot Job.'}
          </p>

          <div className="access-actions">
            <button
              onClick={handleAuthenticatedCheckout}
              disabled={checkoutLoading}
              className="access-btn access-btn--primary"
            >
              {checkoutLoading ? '⏳ Bezig...' : '💳 Start Abonnement (€49/maand)'}
            </button>
            <Link to="/account" className="access-btn access-btn--secondary">
              ⚙️ Account Beheren
            </Link>
          </div>

          {status === 'trialing' && subscription?.days_remaining !== null && (
            <p className="access-trial-info">
              Je proefperiode eindigt over {subscription.days_remaining} dagen
            </p>
          )}

          <div className="access-note">
            <p>
              💡 Heb je al betaald? Refresh de pagina of neem contact op met support.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return children;
}

/**
 * PrivateRoute
 * 
 * Auth0 best practice: Use withAuthenticationRequired HOC
 * This handles authentication automatically and redirects to Auth0 login if needed.
 * It passes appState with returnTo to restore the user's intended destination after login.
 */
function PrivateRoute({ children }) {
  const location = useLocation();

  // Wrap the component with Auth0's recommended HOC
  const Component = withAuthenticationRequired(ProtectedComponent, {
    // Show loading state while checking authentication
    onRedirecting: () => (
      <div className="access-loading">
        <span className="access-loading__icon">🔐</span>
        <p className="access-loading__text">Naar inloggen...</p>
        <p className="access-loading__subtext">Je wordt doorgestuurd naar de login pagina</p>
      </div>
    ),
    // Pass current location to return after login
    loginOptions: {
      appState: {
        returnTo: location.pathname
      }
    }
  });

  return <Component>{children}</Component>;
}

export default PrivateRoute;
